import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ReactComponent as VibecityLogo } from "@/assets/vibecity-logo.svg";

const DIN =
    '"DIN Alternate", "DIN Alternate Bold", "Figtree", "Inter", system-ui, sans-serif';

export default function Header() {
    const { pathname } = useLocation();
    const isApply = pathname.startsWith("/apply");
    // Reactive viewport detection (Header.jsx was previously evaluated once at
    // mount which broke after a desktop->mobile resize and also missed the
    // narrow viewport class on first paint when SSR-style render happened in a
    // wider iframe).
    const [vw, setVw] = useState(
        typeof window !== "undefined" ? window.innerWidth : 1200,
    );
    useEffect(() => {
        const onResize = () => setVw(window.innerWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    const isMobile = vw < 640;
    const isNarrow = vw < 420; // tighter knobs for true-mobile (390 / 360)

    // Size knobs vary between mobile and desktop.
    const btnH = isMobile ? 32 : 41;
    const btnPad = isNarrow ? "5px 9px" : isMobile ? "6px 12px" : "8px 20px";
    const applyPad = isNarrow ? "5px 11px" : isMobile ? "6px 14px" : "8px 20px";
    const btnFs = isNarrow ? 10.5 : isMobile ? 12 : 14;
    const btnLh = isMobile ? "18px" : "24px";
    const radius = isMobile ? 9 : 12;
    const logoH = isNarrow ? 16 : isMobile ? 20 : 26;
    const gapRight = isNarrow ? 5 : isMobile ? 6 : 8;
    const sidePadLeft = isNarrow ? 12 : isMobile ? 16 : 23;
    const sidePadRight = isNarrow ? 10 : isMobile ? 12 : 21;

    return (
        <header
            data-testid="site-header"
            className="fixed top-0 left-0 right-0 z-40"
            style={{
                background: "#0E100F",
                height: 80,
                borderBottom: "0.6px solid rgba(243, 243, 243, 0.27)",
                backdropFilter: "blur(1.2px)",
                WebkitBackdropFilter: "blur(1.2px)",
            }}
        >
            <div className="relative w-full h-full max-w-[1440px] mx-auto px-4 sm:px-10 lg:px-9">
                {/* Left — Vibecity wordmark + divider + "by emergent" */}
                <Link
                    to="/"
                    data-testid="header-logo"
                    aria-label="Vibecity home"
                    className="absolute flex items-center"
                    style={{
                        left: sidePadLeft,
                        top: "calc(50% - 22px)",
                        height: 44,
                        gap: isMobile ? 14 : 18,
                        textDecoration: "none",
                    }}
                >
                    <VibecityLogo
                        aria-hidden
                        style={{
                            height: logoH,
                            width: "auto",
                            color: "#F7F4DA",
                        }}
                    />
                    <span
                        aria-hidden
                        className="hidden sm:inline-block"
                        style={{
                            width: 1,
                            height: 44,
                            background: "#1F1F1F",
                        }}
                    />
                    <span
                        className="hidden sm:inline"
                        style={{
                            fontFamily: DIN,
                            fontWeight: 700,
                            fontSize: 14,
                            lineHeight: "24px",
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            color: "#5A5A5A",
                        }}
                    >
                        by emergent
                    </span>
                </Link>

                {/* Right — Check Status + Apply CTAs */}
                <div
                    className="absolute flex items-center"
                    style={{
                        right: sidePadRight,
                        top: `calc(50% - ${btnH / 2}px)`,
                        height: btnH,
                        gap: gapRight,
                    }}
                >
                    <Link
                        to="/check"
                        data-testid="header-status-link"
                        className="inline-flex items-center justify-center hover:bg-white/[0.03] transition-colors"
                        style={{
                            padding: btnPad,
                            height: btnH,
                            border: "1px solid #9B997F",
                            borderRadius: radius,
                            fontFamily: DIN,
                            fontWeight: 700,
                            fontSize: btnFs,
                            lineHeight: btnLh,
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            color: "#9B997F",
                            textDecoration: "none",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {isNarrow ? "Status" : "Check Status"}
                    </Link>
                    {!isApply && (
                        <Link
                            to="/apply"
                            data-testid="header-apply-cta"
                            className="inline-flex items-center justify-center hover:brightness-105 transition-[filter]"
                            style={{
                                padding: applyPad,
                                height: btnH,
                                background: "#FFFCE1",
                                boxShadow: "inset 0 -2px 6.1px #FFFFFF",
                                borderRadius: 10,
                                fontFamily: DIN,
                                fontWeight: 700,
                                fontSize: btnFs,
                                lineHeight: btnLh,
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                                color: "#0E100F",
                                textDecoration: "none",
                            }}
                        >
                            Apply
                        </Link>
                    )}
                </div>
            </div>
        </header>
    );
}
